// ─────────────────────────────────────────────────────────────────────────────
// THE SHOW SPACE — rungs 0–3, built to `btbshowspacemock.html`.
//
// The mock is the authority for layout, palette, copy, the tap-toggle and the
// concierge. This file renders that design over the REAL wiring in `src/lib/`:
// Supabase identity, POST /app/rank-candidates, chips → POST /app/interests,
// the runtime's why-string verbatim, and the show-side fence. Nothing in
// `src/lib/` changed to accommodate the design.
//
// WHERE THE PROTOTYPE AND THE REAL SYSTEM DIFFER, the real system wins:
//
//   - The mock's toggle flips a fake user ("Alex") on and off with no auth.
//     Here the profiled side requires a real session; signed out it renders in
//     the mock's style but disabled, so a visitor still sees the thing they'd
//     get. The knob label is the signed-in identity, not a hard-coded name.
//
//   - The mock carries a hand-written `whyProf` per episode. Those are demo
//     copy. Real profiled why-text is the runtime's projection of the actual
//     scoring trace and is rendered verbatim — see `src/lib/whyThis.ts`.
//
//   - The mock's episode list is a fixed six with a hard-coded reorder. Here
//     both orders are real: reverse-chronological from the published set, and
//     whatever the fan's own agent returned.
//
// See `showContent.ts` for the three copy-level divergences and their reasons.
// ─────────────────────────────────────────────────────────────────────────────

import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {getFreshAccessToken, signInWithEmail, signOut, supabase} from '../lib/supabase'
import {useShelf, type ShelfMode, type ShelfCard} from '../lib/useShelf'
import {whyThis} from '../lib/whyThis'
import {chipPatch, patchInterests, type ChipDirection} from '../lib/interests'
import {cardAvailability, type Card, type CandidateSet} from '../lib/candidateSet'
import {
  fetchMemberBody,
  joinWithInviteCode,
  memberDoorConfigured,
  MemberDoorError,
} from '../lib/memberDoor'
import {loadGuestRegistry, type GuestEntry} from '../lib/guests'
import {CuttingRoom} from './CuttingRoom'
import {emit as emitBeacon} from '../lib/telemetry'
import {createCut, watchUrl} from '../lib/cutsClient'
import type {Cut} from '../shared/cuts'
import {JumpPlayer} from './JumpPlayer'
import {
  ASK,
  BRAND,
  CARD,
  CAST,
  CAST_HEAD,
  CHIPS_ANON,
  CHIP_REPLY_ANON,
  CONCEPT_PILL,
  DOOR,
  FOOTER,
  HERO,
  KEGS,
  SHELF_HEAD,
  SNUG,
  TAP,
  TAPROOM_HEAD,
} from './showContent'

/** A concierge turn. `who: 'you'` renders as the amber right-aligned bubble. */
interface Turn {
  who: 'snug' | 'you'
  text: string
}

/** Short display name for the tap knob and the concierge greeting. */
function displayName(email: string | null): string {
  const local = (email ?? '').split('@')[0]?.replace(/[^a-zA-Z0-9]/g, '')
  if (!local) return 'YOU'
  return local.slice(0, 7)
}

export function ShowSpace() {
  const [signedIn, setSignedIn] = useState<boolean | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [mode, setMode] = useState<ShelfMode>('anonymous')

  const getToken = useCallback(() => getFreshAccessToken(), [])
  const {state, rank, sendChip} = useShelf(getToken)

  useEffect(() => {
    let live = true
    supabase.auth.getSession().then(({data}) => {
      if (!live) return
      setSignedIn(Boolean(data.session))
      setEmail(data.session?.user?.email ?? null)
    })
    const {data: sub} = supabase.auth.onAuthStateChange((_e, session) => {
      if (!live) return
      setSignedIn(Boolean(session))
      setEmail(session?.user?.email ?? null)
    })
    return () => {
      live = false
      sub.subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (signedIn && state.set) {
      rank(state.set)
      setMode('profiled')
    }
    if (signedIn === false) setMode('anonymous')
  }, [signedIn, state.set, rank])

  const profiled = mode === 'profiled'
  const canProfile = Boolean(signedIn) && Boolean(state.profiled)
  const shelf: ShelfCard[] = profiled && state.profiled ? state.profiled : state.anonymous
  const name = displayName(email)

  return (
    <div className={`wrap show ${profiled ? 'profiled' : ''}`}>
      <TopBar
        profiled={profiled}
        canProfile={canProfile}
        name={name}
        onToggle={() => setMode(m => (m === 'profiled' ? 'anonymous' : 'profiled'))}
        signedIn={Boolean(signedIn)}
        onSignOut={async () => {
          await signOut()
          setMode('anonymous')
        }}
      />

      <section className="hero">
        <div>
          <div className="eyebrow">{HERO.eyebrow(state.set?.cards.length ?? 0)}</div>
          <h1 className="disp">
            {HERO.titleLine1}
            <br />
            <span className="pour">{HERO.titleLine2}</span>
          </h1>
          <p className="lede">{HERO.lede}</p>
          <div className="stats">
            <div>
              <b>{state.set?.cards.length ?? '—'}</b>
              <span>episodes on the shelf</span>
            </div>
            <div>
              <b>{topicCount(state.set?.cards ?? [])}</b>
              <span>topics your agent can match</span>
            </div>
            <div>
              <b>yours</b>
              <span>your profile never leaves you</span>
            </div>
          </div>
        </div>

        <Concierge
          profiled={profiled}
          name={name}
          shelf={shelf}
          getToken={getToken}
          onProfileChanged={() => rank(state.set)}
        />
      </section>

      <section>
        <div className="sec-head">
          <h2 className="disp">{profiled ? SHELF_HEAD.profTitle : SHELF_HEAD.anonTitle}</h2>
          <div className="sub">{profiled ? SHELF_HEAD.profSub : SHELF_HEAD.anonSub}</div>
        </div>
        <div className="mode-note">
          {profiled
            ? SHELF_HEAD.profNote
            : signedIn
              ? SHELF_HEAD.anonNote
              : SHELF_HEAD.signedOutNote}
        </div>

        {state.error ? <p className="notice notice--warn">{state.error}</p> : null}
        {state.degraded && profiled ? (
          <p className="notice notice--warn">
            Your agent couldn’t rank this shelf just now, so it’s in the order the show published it.
          </p>
        ) : null}
        {profiled && state.filtered > 0 ? (
          <p className="notice">
            {state.filtered} {state.filtered === 1 ? 'episode is' : 'episodes are'} hidden by your mutes.
          </p>
        ) : null}

        {state.loading ? (
          <p className="notice">Pouring the shelf…</p>
        ) : (
          <div className="shelf">
            {shelf.map((entry, i) => (
              <EpisodeCard
                key={`${entry.card.id ?? entry.card.title}-${i}`}
                entry={entry}
                profiled={profiled}
                onChip={(d: ChipDirection) => sendChip(entry.card, d)}
              />
            ))}
          </div>
        )}
      </section>

      {!signedIn ? <SignIn /> : null}

      <CuttingRoom signedIn={Boolean(signedIn)} getToken={getToken} />

      <Regulars />

      <Taproom set={state.set} signedIn={Boolean(signedIn)} getToken={getToken} />

      <footer>
        <div className="promise">
          <b>{FOOTER.promiseLead}</b>
          {FOOTER.promiseBody}
        </div>
        <div className="a1">
          {FOOTER.platformLead}
          <b>{FOOTER.platformBrand}</b>
          {FOOTER.platformTail}
          <br />
          {FOOTER.exchange}
        </div>
      </footer>
    </div>
  )
}

function topicCount(cards: Card[]): number | string {
  if (!cards.length) return '—'
  const set = new Set<string>()
  for (const c of cards) for (const t of c.topics ?? []) set.add(t)
  return set.size
}

/* ---- top bar + the signature tap toggle --------------------------------- */

function TopBar({
  profiled,
  canProfile,
  name,
  signedIn,
  onToggle,
  onSignOut,
}: {
  profiled: boolean
  canProfile: boolean
  name: string
  signedIn: boolean
  onToggle: () => void
  onSignOut: () => void
}) {
  return (
    <header>
      <div className="brand">
        <div className="mark disp">
          {BRAND.pre} <b>{BRAND.mid}</b> {BRAND.post}
        </div>
        <div className="concept">{CONCEPT_PILL}</div>
      </div>

      <div className="tap">
        <div className="lbl">
          <b>{profiled ? TAP.profWho(name) : TAP.anonWho}</b>
          <span>{profiled ? TAP.profSub : TAP.anonSub}</span>
          {signedIn ? (
            <>
              {' · '}
              <button className="linkbtn" onClick={onSignOut}>
                sign out
              </button>
            </>
          ) : null}
        </div>
        <button
          aria-label={TAP.ariaLabel}
          aria-pressed={profiled}
          disabled={!canProfile}
          onClick={onToggle}>
          <span className="knob">{profiled ? name.toUpperCase() : TAP.anonKnob}</span>
        </button>
      </div>
    </header>
  )
}

/* ---- the concierge: "The Snug" ------------------------------------------ */

/**
 * The mock's concierge, live.
 *
 * Its chips are conversation openers — distinct from the per-card more/less
 * feedback chips, which live in the why-box. Signed out they are inert and the
 * reply explains why (no network call is made at all). Signed in, each chip is
 * a topic lens drawn from the fan's OWN top-ranked cards: pressing one patches
 * their interest profile and re-ranks, which is the real version of what the
 * mock demonstrates with a fictional user's history.
 *
 * The free-text ask runs the REAL archive search: it posts to /api/cuts, the
 * same prototype endpoint the cutting room uses, and answers with a playable
 * cut of the cited tape. The two ask surfaces on this page therefore behave
 * identically, which is the point — one of them quietly returning a canned
 * line while the other read the transcripts was the worse outcome.
 *
 * F1 (review 2026-08-26): the ask now carries the fan's bearer — solely so the
 * endpoint can refuse anonymous spend and budget per fan; signed out it asks
 * nothing and explains why. No profile data travels with it and nothing about
 * the asker is stored with the cut (see cutsClient.ts / functions/api/cuts).
 * It IS a free-text query (plus identity) leaving the page, which is a real
 * departure from "the show sees nothing", so `SNUG.note` says so in as many
 * words instead of letting the promise cover it.
 */
function Concierge({
  profiled,
  name,
  shelf,
  getToken,
  onProfileChanged,
}: {
  profiled: boolean
  name: string
  shelf: ShelfCard[]
  getToken: () => Promise<string | null>
  onProfileChanged: () => void
}) {
  const [turns, setTurns] = useState<Turn[]>([])
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState(false)
  const [asked, setAsked] = useState<{cut: Cut; saved: boolean} | null>(null)
  const endRef = useRef<HTMLDivElement>(null)

  // The opener is re-dealt whenever the mode flips, exactly as the mock does.
  useEffect(() => {
    setTurns([])
  }, [profiled])

  useEffect(() => {
    endRef.current?.scrollIntoView({block: 'nearest'})
  }, [turns])

  /** Top topics across the fan's highest-ranked cards. */
  const lenses = useMemo(() => {
    if (!profiled) return []
    const counts = new Map<string, number>()
    for (const {card} of shelf.slice(0, 12)) {
      for (const t of card.topics ?? []) counts.set(t, (counts.get(t) ?? 0) + 1)
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([t]) => t)
  }, [profiled, shelf])

  const say = (who: Turn['who'], text: string) => setTurns(t => [...t, {who, text}])

  /**
   * The archive ask — the same POST /api/cuts the cutting room runs, so the
   * answer here is the tape itself rather than a description of it.
   */
  async function askArchive() {
    const question = draft.trim()
    if (question.length < 3 || asking) return
    say('you', question)
    setDraft('')
    setAsked(null)
    setAsking(true)
    say('snug', ASK.working)
    emitBeacon('asked', {question})
    try {
      const result = await createCut(question, await getToken())
      setAsked(result)
      say('snug', ASK.done(result.cut.segments.length))
    } catch (err) {
      const why = err instanceof Error ? err.message : 'it didn’t say why'
      say('snug', /nothing on tape|no timecoded/i.test(why) ? ASK.nothing : ASK.failed(why))
    } finally {
      setAsking(false)
    }
  }

  async function pressLens(topic: string) {
    say('you', `More ${topic}`)
    setBusy(true)
    try {
      const token = await getToken()
      if (!token) {
        say('snug', CHIP_REPLY_ANON)
        return
      }
      await patchInterests(chipPatch([topic], 'more'), token, {
        note: `show-space concierge lens: more ${topic}`,
      })
      onProfileChanged()
      say('snug', `Noted — I’ve leaned your shelf towards ${topic} and re-dealt it. Tap “why this?” on any card to see what changed.`)
    } catch (err) {
      say('snug', `I couldn’t update your profile just then — ${String((err as Error)?.message ?? err)}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="concierge" aria-label="Show concierge">
      <div className="who">
        <div className="dot">{SNUG.avatar}</div>
        <div>
          <b>{SNUG.name}</b>
          <small>{SNUG.tagline}</small>
        </div>
      </div>

      <div>
        <div className="bubble">{profiled ? SNUG.openerProfiled(name) : SNUG.openerAnon}</div>
        {turns.map((t, i) => (
          <div className={`bubble ${t.who === 'you' ? 'you' : ''}`} key={i}>
            {t.text}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="chips">
        {profiled
          ? lenses.map(t => (
              <button key={t} disabled={busy} onClick={() => pressLens(t)}>
                More {t}
              </button>
            ))
          : CHIPS_ANON.map(label => (
              <button
                key={label}
                onClick={() => {
                  say('you', label)
                  say('snug', CHIP_REPLY_ANON)
                }}>
                {label}
              </button>
            ))}
      </div>

      <div className="ask">
        <input
          type="text"
          value={draft}
          maxLength={300}
          placeholder={SNUG.askPlaceholder}
          aria-label="Ask the archive"
          disabled={asking}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') void askArchive()
          }}
        />
        <button disabled={asking || draft.trim().length < 3} onClick={() => void askArchive()}>
          {asking ? 'reading…' : SNUG.askButton}
        </button>
      </div>

      {asked ? (
        <div className="concierge-cut">
          <div className="concierge-cut-head">
            <span>{ASK.resultLead}</span>
            {asked.saved ? (
              <a href={`/watch/${asked.cut.id}`} target="_blank" rel="noreferrer">
                {watchUrl(asked.cut.id)}
              </a>
            ) : null}
          </div>
          <JumpPlayer segments={asked.cut.segments} />
        </div>
      ) : null}

      <div className="note">{SNUG.note}</div>
    </div>
  )
}

/* ---- episode card + why-box --------------------------------------------- */

function EpisodeCard({
  entry,
  profiled,
  onChip,
}: {
  entry: ShelfCard
  profiled: boolean
  onChip: (direction: ChipDirection) => void
}) {
  const [open, setOpen] = useState(false)
  const [sent, setSent] = useState<ChipDirection | null>(null)
  const {card, why} = entry
  const explanation = whyThis(why, {profiled})

  return (
    <article className={`ep ${open ? 'open' : ''}`}>
      <div className="tag">{(card.topics ?? []).join(' · ')}</div>
      <h3>{card.title}</h3>
      {card.summary ? <blockquote>{card.summary}</blockquote> : null}

      <div className="row">
        <a
          className="watch"
          href={card.ref?.url ?? '#'}
          target="_blank"
          rel="noreferrer noopener"
          onClick={() => emitBeacon('clicked', {topic: card.topics?.[0], cardId: card.id})}>
          {CARD.watch}
        </a>
        <button className="why" aria-expanded={open} onClick={() => setOpen(v => !v)}>
          {CARD.why}
        </button>
      </div>

      <div className="whybox" role="note">
        {/* Profiled: the runtime's projection of the real scoring trace, verbatim.
            Anonymous: the publisher's own note, if the set carries one. */}
        {profiled ? explanation.trace : (card.editorialNote ?? null)}
        <span className="blind">{explanation.blind}</span>

        {profiled ? (
          <div className="feedback">
            <button
              className={sent === 'more' ? 'is-sent' : ''}
              onClick={() => {
                setSent('more')
                onChip('more')
              }}>
              More like this
            </button>
            <button
              className={sent === 'less' ? 'is-sent' : ''}
              onClick={() => {
                setSent('less')
                onChip('less')
              }}>
              Less like this
            </button>
          </div>
        ) : null}
      </div>
    </article>
  )
}

/* ---- the regulars → guest spaces ---------------------------------------- */

/**
 * "The regulars", now the doorway into guest spaces v0: guests in the public
 * registry link to their own page (#/guest/<slug>); the mock's cast entries
 * without a registry page render as before. The registry fetch is a bare
 * same-origin GET (fence-checked in loadGuestRegistry).
 */
function Regulars() {
  const [registry, setRegistry] = useState<GuestEntry[]>([])

  useEffect(() => {
    let live = true
    loadGuestRegistry()
      .then(gs => {
        if (live) setRegistry(gs)
      })
      .catch(() => {
        /* no registry — the section renders the mock cast unchanged */
      })
    return () => {
      live = false
    }
  }, [])

  const registryNames = new Set(registry.map(g => g.name))
  const mockOnly = CAST.filter(g => !registryNames.has(g.name))

  return (
    <section>
      <div className="sec-head">
        <h2 className="disp">{CAST_HEAD.title}</h2>
        <div className="sub">{CAST_HEAD.sub}</div>
      </div>
      <div className="cast">
        {registry.map(g => (
          <a className="guest guest--link" key={g.slug} href={`#/guest/${g.slug}`}>
            <div className="av">{g.initials}</div>
            <div>
              <b>{g.name}</b>
              <span>
                {g.note} · {CAST_HEAD.follow}
              </span>
            </div>
          </a>
        ))}
        {mockOnly.map(g => (
          <div className="guest" key={g.name}>
            <div className="av">{g.initials}</div>
            <div>
              <b>{g.name}</b>
              <span>
                {g.note} · {CAST_HEAD.follow}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

/* ---- the taproom -------------------------------------------------------- */

/**
 * The taproom, real (Ticket 9): member-availability cards from the published
 * set render title+summary+lock for everyone — the door. Tapping a locked card
 * EXPLAINS membership (never before — the design filter); the invite-code
 * redemption + the gated body fetch go to the member service, which resolves
 * effective membership server-side. When the set carries no member cards the
 * mock's kegs render unchanged.
 */
function Taproom({
  set,
  signedIn,
  getToken,
}: {
  set: CandidateSet | null
  signedIn: boolean
  getToken: () => Promise<string | null>
}) {
  const [reply, setReply] = useState<string | null>(null)
  const [showCode, setShowCode] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [bodies, setBodies] = useState<Record<string, {contentUrl?: string; note?: string}>>({})

  const memberCards = (set?.cards ?? []).filter(c => cardAvailability(c) === 'member')

  async function knock(card: Card) {
    // The door is explained ON the knock — this handler is the only place the
    // membership copy enters the page.
    if (!memberDoorConfigured()) {
      setReply(`${DOOR.explain} ${DOOR.notOpenYet}`)
      return
    }
    if (!signedIn) {
      setReply(DOOR.signedOut)
      return
    }
    setBusy(true)
    try {
      const token = await getToken()
      const out = await fetchMemberBody(String(card.id), token)
      const body = (out.body ?? {}) as {contentUrl?: string; note?: string}
      setBodies(b => ({...b, [String(card.id)]: body}))
      setReply(null)
    } catch (err) {
      if (err instanceof MemberDoorError && err.status === 403) {
        setReply(DOOR.explain)
        setShowCode(true)
      } else {
        setReply(String((err as Error)?.message ?? err))
      }
    } finally {
      setBusy(false)
    }
  }

  async function redeem() {
    if (!code.trim() || !set?.membershipGroup) return
    setBusy(true)
    try {
      const token = await getToken()
      await joinWithInviteCode(set.membershipGroup, code.trim(), token)
      setReply(DOOR.invited)
      setShowCode(false)
      setCode('')
    } catch {
      setReply(DOOR.codeRejected)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section>
      <div className="sec-head">
        <h2 className="disp">{TAPROOM_HEAD.title}</h2>
        <div className="sub">{TAPROOM_HEAD.sub}</div>
      </div>
      <div className="taproom">
        {memberCards.length
          ? memberCards.map(card => {
              const body = bodies[String(card.id)]
              return (
                <div className="keg" key={card.id}>
                  <span className="lock">{body ? DOOR.member : TAPROOM_HEAD.lock}</span>
                  <h3>{card.title}</h3>
                  <p>{card.summary}</p>
                  {body ? (
                    body.contentUrl ? (
                      <a className="watch" href={body.contentUrl} target="_blank" rel="noreferrer noopener">
                        {DOOR.open}
                      </a>
                    ) : (
                      <p>{body.note ?? DOOR.member}</p>
                    )
                  ) : (
                    <button disabled={busy} onClick={() => knock(card)}>
                      {TAPROOM_HEAD.cta}
                    </button>
                  )}
                </div>
              )
            })
          : KEGS.map(keg => (
              <div className="keg" key={keg.id}>
                <span className="lock">{TAPROOM_HEAD.lock}</span>
                <h3>{keg.title}</h3>
                <p>{keg.blurb}</p>
                <button onClick={() => setReply(keg.reply)}>{TAPROOM_HEAD.cta}</button>
              </div>
            ))}
      </div>
      {reply ? (
        <div className="concierge taproom-reply">
          <div className="who">
            <div className="dot">{SNUG.avatar}</div>
            <div>
              <b>{SNUG.name}</b>
            </div>
          </div>
          <div className="bubble">{reply}</div>
          {showCode ? (
            <div className="ask">
              <input
                type="text"
                value={code}
                placeholder={DOOR.codePlaceholder}
                aria-label={DOOR.codePlaceholder}
                onChange={e => setCode(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') redeem()
                }}
              />
              <button disabled={busy || !code.trim()} onClick={redeem}>
                {DOOR.codeButton}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

/* ---- sign in ------------------------------------------------------------ */

function SignIn() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<string | null>(null)

  return (
    <section>
      <div className="signin">
        <h2 className="disp">Pull up a stool</h2>
        <p>
          Sign in and your own agent re-deals this shelf against what you’re into. The ranking runs in your
          agent, on your side — Between Two Beers never sees your profile, only that someone fetched the shelf.
        </p>
        <form
          onSubmit={async e => {
            e.preventDefault()
            setStatus('Sending…')
            const {error} = await signInWithEmail(email)
            setStatus(error ?? 'Check your email for the link.')
          }}>
          <input
            type="email"
            required
            value={email}
            placeholder="you@example.com"
            onChange={e => setEmail(e.target.value)}
          />
          <button type="submit">Send link</button>
        </form>
        {status ? <p className="notice" style={{marginTop: 12, marginBottom: 0}}>{status}</p> : null}
      </div>
    </section>
  )
}
